// WHERE A FRAME'S TIME GOES (owner, 2026-10-19: before deciding whether to
// replace three.js with a Rust / WebGPU engine, "start with the
// measurement").
//
// perfSnapshot() already says how long frames take, how many draw calls and
// triangles they carry and what was fetched. It cannot say WHICH WORK the
// time went to - our JavaScript (the streaming tick, admission, culling,
// isolation tests, materialising instances, rebuilding geometry off the
// workers) or the GPU. That split is the whole question: a new engine only
// pays if the GPU side, or three.js's own submit, is where the time is.
//
// window.__sl.profile(seconds) records for that long and answers:
//   frames     interval avg / p95, fps, frames over 33 ms
//   render     CPU time inside renderer.render / composer.render (three.js
//              walking the scene and issuing GL calls)
//   gpu        GPU time per frame from EXT_disjoint_timer_query_webgl2,
//              when the browser exposes it (null otherwise - said, not 0)
//   tick       the streaming controller's tick, and the costliest of the
//              controller / materialiser / applicator methods (inclusive)
//   decode     worker round trip per tile (queue + parse) and the
//              main-thread rebuild of what came back
//   longTasks  main-thread tasks over 50 ms (PerformanceObserver)
//   heap       JS heap at start, end and peak (performance.memory, Chrome)
//   loaded     tiles / fetches / bytes over the window, from perfSnapshot
//
// OFF UNTIL ASKED. Nothing here runs or wraps anything until profile() is
// called, and every wrap is undone when it finishes - a viewer that is not
// being measured pays nothing.

import PackDecodePool from './PackDecodePool';

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const round = (v) => Math.round(v * 100) / 100;
const pct = (arr, p) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(s.length * p))];
};
const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

// Wrap every method of `obj` (own and prototype, not getters) to time it.
// Returns an undo function. Inclusive times: a method that calls another
// counts both.
export function timeMethods(obj, label, ledger, { only = null, skip = [] } = {}) {
  if (!obj) return () => {};
  const names = new Set();
  let p = obj;
  while (p && p !== Object.prototype) {
    for (const n of Object.getOwnPropertyNames(p)) {
      if (n === 'constructor' || skip.includes(n)) continue;
      if (only && !only.includes(n)) continue;
      const d = Object.getOwnPropertyDescriptor(p, n);
      if (d && typeof d.value === 'function' && !d.get) names.add(n);
    }
    p = Object.getPrototypeOf(p);
  }
  const undo = [];
  for (const n of names) {
    const orig = obj[n];
    const had = Object.prototype.hasOwnProperty.call(obj, n);
    const key = `${label}.${n}`;
    obj[n] = function timed(...args) {
      const t = now();
      try {
        const out = orig.apply(this, args);
        if (out && typeof out.then === 'function') {
          // async: count the main-thread part only (to the first await)
          return out;
        }
        return out;
      } finally {
        const row = ledger[key] || (ledger[key] = { calls: 0, ms: 0, max: 0 });
        const dt = now() - t;
        row.calls += 1; row.ms += dt; if (dt > row.max) row.max = dt;
      }
    };
    undo.push(() => { if (had) obj[n] = orig; else delete obj[n]; });
  }
  return () => undo.forEach((u) => u());
}

/** Record for `seconds` and answer where the time went. */
export async function profile(loader, seconds = 10) {
  const ms = Math.max(1, Math.min(120, Number(seconds) || 10)) * 1000;
  const viewer = loader && loader.viewer;
  const controller = loader && loader.streamingController;
  const ledger = {};
  const undo = [];
  const before = typeof loader.perfSnapshot === 'function' ? safe(() => loader.perfSnapshot()) : null;

  // ---- frames ----------------------------------------------------------
  const intervals = [];
  let last = null; let running = true;
  const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (f) => setTimeout(() => f(now()), 16);
  const onFrame = (t) => { if (last !== null) intervals.push(t - last); last = t; if (running) raf(onFrame); };
  raf(onFrame);

  // ---- render (CPU) + GPU timer -----------------------------------------
  const renderMs = [];
  const gpuMs = [];
  const renderer = viewer && viewer.renderer;
  const gl = renderer && typeof renderer.getContext === 'function' ? renderer.getContext() : null;
  const ext = gl && typeof gl.getExtension === 'function' ? gl.getExtension('EXT_disjoint_timer_query_webgl2') : null;
  const queries = [];
  const pollQueries = () => {
    if (!ext || !gl) return;
    for (let i = queries.length - 1; i >= 0; i -= 1) {
      const q = queries[i];
      if (!gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) continue;
      if (!gl.getParameter(ext.GPU_DISJOINT_EXT)) gpuMs.push(gl.getQueryParameter(q, gl.QUERY_RESULT) / 1e6);
      gl.deleteQuery(q);
      queries.splice(i, 1);
    }
  };
  const wrapRender = (target, name) => {
    if (!target || typeof target[name] !== 'function') return;
    const orig = target[name];
    target[name] = function timedRender(...args) {
      let q = null;
      if (ext && gl && queries.length < 8) { q = gl.createQuery(); gl.beginQuery(ext.TIME_ELAPSED_EXT, q); }
      const t = now();
      try { return orig.apply(this, args); } finally {
        renderMs.push(now() - t);
        if (q) { gl.endQuery(ext.TIME_ELAPSED_EXT); queries.push(q); }
        pollQueries();
      }
    };
    undo.push(() => { target[name] = orig; });
  };
  wrapRender(renderer, 'render');
  if (viewer && viewer.postProcessing && viewer.postProcessing.composer) wrapRender(viewer.postProcessing.composer, 'render');

  // ---- our JavaScript ---------------------------------------------------
  undo.push(timeMethods(controller, 'controller', ledger));
  if (controller && controller.materializer) undo.push(timeMethods(controller.materializer, 'materializer', ledger));
  const store = viewer && viewer.streaming && viewer.streaming.state;
  if (store) undo.push(timeMethods(store, 'store', ledger, { skip: ['subscribe'] }));
  const applicator = (viewer && viewer.streaming && viewer.streaming.applicator) || (controller && controller.applicator);
  if (applicator) undo.push(timeMethods(applicator, 'applicator', ledger));

  // ---- decode: worker round trip + main-thread rebuild -------------------
  const decode = { roundTripMs: [], rebuildMs: [] };
  const pool = loader._workerPool;
  if (pool && typeof pool.parseGLB === 'function') {
    const origParse = pool.parseGLB;
    pool.parseGLB = function timedParse(...args) {
      const t = now();
      const out = origParse.apply(this, args);
      if (out && typeof out.then === 'function') out.then(() => decode.roundTripMs.push(now() - t), () => {});
      return out;
    };
    undo.push(() => { pool.parseGLB = origParse; });
    const origReply = pool._onReply;
    pool._onReply = function timedReply(...args) {
      const t = now();
      try { return origReply.apply(this, args); } finally { decode.rebuildMs.push(now() - t); }
    };
    undo.push(() => { pool._onReply = origReply; });
  }

  // Packs (the format most tiles arrive in) decode in their own worker pool,
  // made on first use - so the prototype is what gets timed.
  const packProto = PackDecodePool && PackDecodePool.prototype;
  if (packProto && typeof packProto.decode === 'function') {
    const origDecode = packProto.decode;
    packProto.decode = function timedDecode(...args) {
      const t = now();
      const out = origDecode.apply(this, args);
      if (out && typeof out.then === 'function') out.then(() => decode.roundTripMs.push(now() - t), () => {});
      return out;
    };
    undo.push(() => { packProto.decode = origDecode; });
  }
  const fetcher = controller && controller.fetcher;
  const packBefore = fetcher && fetcher._packStats ? { ...fetcher._packStats } : null;

  // ---- long tasks, heap ------------------------------------------------
  const longTasks = [];
  let observer = null;
  try {
    if (typeof PerformanceObserver !== 'undefined') {
      observer = new PerformanceObserver((list) => list.getEntries().forEach((e) => longTasks.push(e.duration)));
      observer.observe({ entryTypes: ['longtask'] });
    }
  } catch (e) { observer = null; }
  const mem = () => (typeof performance !== 'undefined' && performance.memory ? performance.memory.usedJSHeapSize / 1048576 : null);
  const heap = { startMb: mem(), peakMb: mem(), endMb: null };
  const heapTimer = setInterval(() => { const m = mem(); if (m !== null && (heap.peakMb === null || m > heap.peakMb)) heap.peakMb = m; }, 500);

  const t0 = now();
  await new Promise((r) => setTimeout(r, ms));
  const elapsed = now() - t0;
  running = false;
  clearInterval(heapTimer);
  heap.endMb = mem();
  if (observer) try { observer.disconnect(); } catch (e) { /* gone */ }
  pollQueries();
  undo.reverse().forEach((u) => { try { u(); } catch (e) { /* restored as far as possible */ } });
  const after = typeof loader.perfSnapshot === 'function' ? safe(() => loader.perfSnapshot()) : null;
  const packAfter = fetcher && fetcher._packStats ? { ...fetcher._packStats } : null;
  const packDelta = packAfter ? Object.fromEntries(Object.entries(packAfter)
    .filter(([, v]) => typeof v === 'number')
    .map(([k, v]) => [k, round(v - ((packBefore && packBefore[k]) || 0))])) : null;

  const frames = intervals.length;
  const sumMs = (rows) => rows.reduce((a, b) => a + b, 0);
  const js = Object.entries(ledger)
    .map(([k, r]) => ({ fn: k, calls: r.calls, totalMs: round(r.ms), perFrameMs: frames ? round(r.ms / frames) : 0, maxMs: round(r.max) }))
    .sort((a, b) => b.totalMs - a.totalMs);
  const tickRow = ledger['controller.tick'] || { ms: 0, calls: 0 };
  return {
    seconds: round(elapsed / 1000),
    frames: {
      count: frames, intervalAvgMs: round(avg(intervals)), intervalP95Ms: round(pct(intervals, 0.95)),
      fps: frames ? round(1000 / avg(intervals)) : 0, over33ms: intervals.filter((x) => x > 33.4).length,
    },
    render: { calls: renderMs.length, cpuAvgMs: round(avg(renderMs)), cpuP95Ms: round(pct(renderMs, 0.95)), cpuTotalMs: round(sumMs(renderMs)) },
    gpu: ext ? { samples: gpuMs.length, avgMs: round(avg(gpuMs)), p95Ms: round(pct(gpuMs, 0.95)) } : null,
    gpuNote: ext ? '' : 'EXT_disjoint_timer_query_webgl2 not exposed by this browser - GPU time not measured',
    tick: { calls: tickRow.calls, totalMs: round(tickRow.ms), share: elapsed ? round((tickRow.ms / elapsed) * 100) : 0 },
    decode: {
      tiles: decode.roundTripMs.length, roundTripAvgMs: round(avg(decode.roundTripMs)), roundTripP95Ms: round(pct(decode.roundTripMs, 0.95)),
      rebuildTotalMs: round(sumMs(decode.rebuildMs)), rebuildAvgMs: round(avg(decode.rebuildMs)),
      // packs fetched / bytes / worker decodes / main-thread decodeMs over the window
      packs: packDelta,
    },
    longTasks: { count: longTasks.length, totalMs: round(sumMs(longTasks)), maxMs: round(longTasks.length ? Math.max(...longTasks) : 0) },
    heap: { startMb: heap.startMb === null ? null : round(heap.startMb), peakMb: heap.peakMb === null ? null : round(heap.peakMb), endMb: heap.endMb === null ? null : round(heap.endMb) },
    mainThreadJs: js.slice(0, 25),
    snapshot: { before: pick(before), after: pick(after) },
  };
}

function safe(fn) { try { return fn(); } catch (e) { return null; } }
function pick(s) {
  if (!s) return null;
  return {
    drawCalls: s.gpu && s.gpu.drawCalls, triangles: s.gpu && s.gpu.triangles, geometries: s.gpu && s.gpu.geometries,
    fetches: s.fetches || null, frame: s.frame || null,
  };
}

export default profile;
